import { HttpClient } from '@angular/common/http';
import { Service, computed, effect, inject, signal } from '@angular/core';
import { SaphirTenantContext } from '@saphir/module-contract';

import { environment } from '../../environments/environment';
import { ServiceCompanyAccess, ServiceUser } from '../core/umc/service-api.model';
import { ModuleRegistryService } from './module-registry.service';

const ACTIVE_COMPANY_KEY = 'saphir-web.active-company';

type LoadState = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * The user's companies and permissions, from the User Management Center.
 *
 * Loaded once per session (`AuthService` triggers it after sign-in) from
 * `GET /service/me` — the one call UMC's integration surface answers "who is
 * this person and what may they do" with. The user picks the ACTIVE company;
 * its effective permissions are what `SAPHIR_AUTH.permissions` exposes to
 * every module, so switching company re-evaluates everything
 * permission-gated at once.
 *
 * Effective permissions merge both planes of the answer: the company's
 * UMC-plane codes (`users.manage`, …) and the fully-qualified module verbs
 * (`payroll.read`, …). UMC qualifies verbs with the module's SALES code,
 * while a module checks rights under its own id (`lohn.read`) — so verbs are
 * additionally re-prefixed through the registry's `licenseCode` mapping,
 * keeping both spellings in the set.
 *
 * Failure is deliberately quiet: an unreachable UMC costs the permissions and
 * the switcher, nothing else — the shell must not turn a sidecar outage into
 * a blank screen. The error goes to the console; the state stays `failed` so
 * nothing retries on every render.
 *
 * Mock mode ships two fake companies with different permission sets, so the
 * switcher — and everything gated behind permissions — can be developed
 * without a running UMC.
 */
@Service()
export class CompanyContextService implements SaphirTenantContext {
  private readonly http = inject(HttpClient);
  private readonly registry = inject(ModuleRegistryService);

  private readonly state = signal<LoadState>('idle');
  private readonly context = signal<ServiceUser | null>(null);

  /** The user's explicit choice; null means "the first membership". */
  private readonly chosenCompanyId = signal<number | null>(restoreChoice());

  readonly loadState = this.state.asReadonly();

  /**
   * Companies the user can act in. `/service/me` reports active memberships
   * only — a pending invitation is deliberately not in the answer.
   */
  readonly memberships = computed<readonly ServiceCompanyAccess[]>(
    () => this.context()?.companies ?? [],
  );

  readonly activeMembership = computed<ServiceCompanyAccess | null>(() => {
    const memberships = this.memberships();
    const chosen = memberships.find((m) => m.companyId === this.chosenCompanyId());

    if (chosen) {
      return chosen;
    }

    // No default until the real memberships are in: reporting a provisional
    // company — instead of the contract's `null` — would flip the tenant
    // context once the answer arrives, and a hosted module that restarts on a
    // company switch (Fakt) would reload the page on every boot.
    if (this.state() !== 'ready') {
      return null;
    }

    return memberships[0] ?? null;
  });

  /**
   * What `SAPHIR_AUTH.permissions` exposes: the active company's UMC codes
   * and module verbs, the latter under the UMC sales code AND the shell
   * module id (`payroll.read` → also `lohn.read`), so a module can check
   * rights under the id it knows itself by.
   */
  readonly activePermissions = computed<readonly string[]>(() => {
    const membership = this.activeMembership();

    if (!membership) {
      return [];
    }

    const verbs = membership.modules.flatMap((module) => module.permissions);

    return dedupe([...membership.permissions, ...verbs, ...this.translateVerbs(verbs)]);
  });

  /**
   * `SaphirTenantContext` — what a hosted module sees of the choice above.
   * Backend routes carry this id (`/api/v1/tenants/{id}/…`), and
   * `companyIdInterceptor` stamps it on API requests as `X-Company-Id`.
   */
  readonly activeCompanyId = computed<number | null>(
    () => this.activeMembership()?.companyId ?? null,
  );

  readonly activeCompanyName = computed<string | null>(
    () => this.activeMembership()?.companyName ?? null,
  );

  /**
   * The customer the active company belongs to — the business the user works
   * for, above the tenant they picked. Null when UMC does not send it.
   */
  readonly activeCustomerName = computed<string | null>(
    () => this.activeMembership()?.customerName ?? null,
  );

  /**
   * The user founded a customer whose business card is incomplete. UMC refuses
   * everything but `/service/me` until it is filled in, so the shell offers no
   * module and points to the card instead.
   */
  readonly cardIncomplete = computed(() => this.context()?.cardIncomplete === true);

  readonly isSuperAdmin = computed(() => this.context()?.isSuperAdmin ?? false);

  /** Display name as UMC knows it — often better than the token's. */
  readonly displayName = computed(() => this.context()?.displayName ?? null);

  constructor() {
    // The registry decides which modules this viewer is offered, and staff-only
    // ones are offered to nobody until it knows who is looking. Pushed rather
    // than pulled: the registry is built during application start, before any
    // of this is known, and it cannot inject this service back — this one
    // already depends on IT, for permission-verb translation.
    effect(() => {
      this.registry.setViewerIsSuperAdmin(this.isSuperAdmin());
    });
  }

  /** Idempotent: the context is loaded once, however many callers ask. */
  load(): void {
    if (this.state() !== 'idle') {
      return;
    }

    if (environment.auth.useMockAuth) {
      this.state.set('ready');
      this.context.set(MOCK_CONTEXT);

      return;
    }

    this.state.set('loading');
    this.http.get<ServiceUser>(`${environment.umc.baseUrl}/service/me`).subscribe({
      next: (context) => {
        this.context.set(context);
        this.state.set('ready');
      },
      error: (error: unknown) => {
        this.state.set('failed');
        console.error('[umc] /service/me failed — no permissions, no switcher.', error);
      },
    });
  }

  setActiveCompany(companyId: number): void {
    this.chosenCompanyId.set(companyId);

    try {
      localStorage.setItem(ACTIVE_COMPANY_KEY, String(companyId));
    } catch {
      // Storage unavailable — the choice still holds for this tab.
    }
  }

  /**
   * Re-prefixes verbs from a module's UMC sales code onto its shell id —
   * `payroll.read` becomes `lohn.read` for the module the config maps.
   * Modules whose id doubles as the code need no translation.
   */
  private translateVerbs(verbs: readonly string[]): string[] {
    const idByCode = new Map(
      this.registry
        // Audience-independent: a verb arrives for a module this viewer may not
        // see, and it still has to be translated rather than left qualified
        // with a code nothing matches.
        .allModules()
        .filter((module) => module.licenseCode && module.licenseCode !== module.id)
        .map((module) => [module.licenseCode as string, module.id]),
    );

    return verbs.flatMap((verb) => {
      const dot = verb.indexOf('.');
      const id = dot > 0 ? idByCode.get(verb.slice(0, dot)) : undefined;

      return id ? [`${id}${verb.slice(dot)}`] : [];
    });
  }
}

function dedupe(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function restoreChoice(): number | null {
  try {
    const raw = localStorage.getItem(ACTIVE_COMPANY_KEY);
    const parsed = raw === null ? NaN : Number(raw);

    return Number.isInteger(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Two companies with different rights, so switching visibly changes things.
 * Wire-shaped — the same mapping code runs over it that runs over the real
 * answer. Verbs carry the UMC sales code `payroll`; the registry maps them
 * onto the `lohn` module id.
 */
const MOCK_CONTEXT: ServiceUser = {
  userId: 1,
  email: 'developer@example.test',
  displayName: 'developer',
  isSuperAdmin: false,
  status: 'active',
  companies: [
    {
      companyId: 1,
      companyName: 'Saphir Software GmbH',
      companyShortName: 'Saphir',
      companyColor: '#3b5bdb',
      customerId: 1,
      customerName: 'Saphir Software GmbH',
      roleCode: 'admin',
      roleLabel: 'Administrator',
      isOwner: true,
      permissions: ['users.manage'],
      modules: [
        {
          moduleCode: 'payroll',
          moduleLabel: 'Lohn',
          // The payrollManager verb set, abridged: the admin company must see
          // every control the module gates — the "new employee" button needs
          // `payroll.create`, parts of the detail screen `payroll.manage`.
          permissions: [
            'payroll.read',
            'payroll.write',
            'payroll.create',
            'payroll.manage',
            // md01b: change history and bulk import — until UMC confirms the verbs (OP-MD-4).
            'payroll.import',
            'payroll.auditlog',
          ],
        },
      ],
    },
    {
      companyId: 2,
      companyName: 'Muster AG',
      companyShortName: 'Muster',
      companyColor: '#e8590c',
      customerId: 2,
      customerName: 'Muster AG',
      roleCode: 'member',
      roleLabel: 'Member',
      isOwner: false,
      permissions: [],
      // No live payroll licence (see the licenses mock), so UMC would answer
      // no module access here — an empty list, not a reduced verb set.
      modules: [],
    },
  ],
};
